import { useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Coins,
  HandCoins,
  ScrollText,
  ShieldCheck,
  Undo2,
} from "lucide-react";
import RecordMovementModal from "./RecordMovementModal";
import { CashMovementType } from "@/redux/services/pos/pos.type";
import { usePermissions } from "@/hooks/usePermissions";

interface QuickMovementActionsProps {
  restaurantId: string;
  sessionUuid: string;
  currency: string;
}

interface ActionItem {
  type: CashMovementType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  effect: "positive" | "negative" | "variable";
}

const ACTIONS: ActionItem[] = [
  {
    type: "pay_in",
    label: "Approvisionnement",
    icon: ArrowDownToLine,
    effect: "positive",
  },
  {
    type: "pay_out",
    label: "Sortie de caisse",
    icon: ArrowUpFromLine,
    effect: "negative",
  },
  {
    type: "tip_in",
    label: "Pourboires reçus",
    icon: Coins,
    effect: "positive",
  },
  {
    type: "tip_out",
    label: "Pourboires retirés",
    icon: HandCoins,
    effect: "negative",
  },
  {
    type: "refund",
    label: "Remboursement",
    icon: Undo2,
    effect: "negative",
  },
  {
    type: "correction",
    label: "Correction",
    icon: ScrollText,
    effect: "variable",
  },
  {
    type: "cash_drop",
    label: "Dépôt coffre",
    icon: ShieldCheck,
    effect: "negative",
  },
];

const QuickMovementActions = ({
  restaurantId,
  sessionUuid,
  currency,
}: QuickMovementActionsProps) => {
  const { can } = usePermissions();
  // Recording cash movements is its own capability. Cashiers hold it by default
  // (it ships in their role bundle), others need it granted explicitly.
  const canRecord = can("cash_register.movement");
  const [openType, setOpenType] = useState<CashMovementType | null>(null);

  if (!canRecord) return null;

  return (
    <div className="dash__card p-6 h-full flex flex-col">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">Enregistrer un mouvement</h3>
        <p className="text-sm text-muted-foreground">
          Choisissez le type pour démarrer l'enregistrement.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
        {ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.type}
              type="button"
              onClick={() => setOpenType(action.type)}
              className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
            >
              <span
                className={
                  action.effect === "positive"
                    ? "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-success/10 text-success"
                    : action.effect === "negative"
                    ? "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-destructive/10 text-destructive"
                    : "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
                }
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-card-foreground">
                {action.label}
              </span>
            </button>
          );
        })}
      </div>

      {openType && (
        <RecordMovementModal
          isOpen={!!openType}
          onClose={() => setOpenType(null)}
          restaurantId={restaurantId}
          sessionUuid={sessionUuid}
          currency={currency}
          initialType={openType}
        />
      )}
    </div>
  );
};

export default QuickMovementActions;
